import { Link } from 'react-router-dom';
import { MapPin, Building2 } from 'lucide-react';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatPriceRange } from '@/lib/formatPrice';
import { optimizedImageUrl } from '@/lib/imageOptimizer';

interface Props {
  project: any;
  lang?: string;
}

export function ProjectMapCard({ project, lang = 'en' }: Props) {
  const { displayCurrency, rate } = useCurrency();
  const name = typeof project.name === 'object'
    ? (project.name?.[lang] || project.name?.en || '')
    : (project.name || '');
  const imgRaw = project.project_images?.find((i: any) => i.is_primary)?.url
    || project.project_images?.[0]?.url
    || 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&h=300&fit=crop';
  const img = optimizedImageUrl(imgRaw, { width: 280, height: 200, quality: 70 });
  const slug = project.slug || project.id;
  const priceLabel = project.price_min || project.price_max
    ? formatPriceRange(project.price_min, project.price_max, project.currency || 'ETB', displayCurrency, rate)
    : 'Price on Request';
  const status = project.status?.replace('_', ' ');
  const developer = project.developer_profiles?.company_name;

  return (
    <Link
      to={`/projects/${slug}`}
      className="flex gap-3 p-2.5 rounded-xl border border-border bg-card hover:border-primary/40 hover:shadow-sm transition-all"
    >
      <img src={img} alt={name} loading="lazy" className="w-28 h-24 rounded-lg object-cover shrink-0" />
      <div className="flex-1 min-w-0 flex flex-col py-0.5">
        <h3 className="text-sm font-bold leading-tight line-clamp-1">{name}</h3>
        {developer && (
          <p className="text-[10px] text-muted-foreground flex items-center gap-1 mt-0.5 truncate">
            <Building2 className="h-2.5 w-2.5" />
            <span className="truncate">{developer}</span>
          </p>
        )}
        <p className="text-[10px] text-muted-foreground flex items-center gap-1 mt-0.5">
          <MapPin className="h-2.5 w-2.5" />
          <span className="truncate">{project.city || ''}{project.sub_city ? `, ${project.sub_city}` : ''}</span>
        </p>
        <div className="mt-auto flex items-center justify-between gap-2 pt-1">
          <span className="text-xs font-bold text-primary truncate">{priceLabel}</span>
          {status && (
            <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground shrink-0 capitalize">
              {status}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
